import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api, ensureOk } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Paginador } from '@/components/Paginador';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { fechaHora, fechaLarga, mxn } from '@/lib/formato';

import { useEquipos, type Equipo } from './queries';

const CLAVES = ['status'] as const;
const ESTADOS: Record<Equipo['status'], string> = {
  pending: 'Pendiente',
  partial: 'Pago parcial',
  paid: 'Pagado',
};

function Equipos() {
  const { filtros, cambiar } = useFiltrosUrl(CLAVES);
  const editable = useCan('tienda', 'editar');
  const qc = useQueryClient();
  const q = useEquipos({
    page: filtros.page,
    ...(filtros.status && { status: filtros.status as Equipo['status'] }),
  });
  const cambiarEstado = useMutation({
    mutationFn: ({ id, status }: { id: number; status: Equipo['status'] }) =>
      ensureOk(
        api.PATCH('/api/v1/admin/purchase-groups/{id}', {
          params: { path: { id: String(id) } },
          body: { status },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tienda', 'equipos'] });
      toast.success('Estado actualizado.');
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <>
      <Encabezado
        titulo="Compras en equipo"
        descripcion="Grupos que compran juntos con precio por persona."
      />
      <select
        className="input-vidrio mb-4 max-w-48"
        value={filtros.status}
        onChange={(e) => cambiar({ status: e.target.value })}
        aria-label="Estado"
      >
        <option value="">Todos</option>
        {Object.entries(ESTADOS).map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </select>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.data.length === 0 && <Vacio titulo="Sin compras en equipo" />}
      <div className="space-y-4">
        {q.data?.data.map((g) => (
          <section key={g.id} className="panel-vidrio space-y-3">
            <header className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-extrabold">{g.name}</h2>
                <p className="text-sm text-texto-2">
                  {g.membershipName} ·{' '}
                  {g.paymentMode === 'together' ? 'Pago conjunto' : 'Pago individual'} · organiza{' '}
                  {g.organizerName ?? '—'} ({g.organizerEmail ?? '—'}) · creado{' '}
                  {fechaHora(g.createdAt)}
                  {g.deadlineOn && ` · fecha límite ${fechaLarga(g.deadlineOn)}`}
                </p>
              </div>
              <select
                className="input-vidrio max-w-44"
                value={g.status}
                disabled={!editable || cambiarEstado.isPending}
                onChange={(e) =>
                  cambiarEstado.mutate({ id: g.id, status: e.target.value as Equipo['status'] })
                }
                aria-label="Estado del grupo"
              >
                {Object.entries(ESTADOS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </header>
            <div className="overflow-x-auto">
              <table className="tabla-cs w-full text-sm">
                <thead>
                  <tr>
                    <th>Integrante</th>
                    <th>Correo</th>
                    <th>Teléfono</th>
                    <th>Precio</th>
                    <th>Pago</th>
                  </tr>
                </thead>
                <tbody>
                  {g.members.map((m) => (
                    <tr key={m.id}>
                      <td>
                        {m.name}
                        {m.role === 'organizer' && <span className="insignia ml-1">Organiza</span>}
                      </td>
                      <td>{m.email}</td>
                      <td>{m.phone ?? '—'}</td>
                      <td className="tabular">{mxn(m.finalPrice)}</td>
                      <td>
                        {m.paymentStatus === 'paid' ? (
                          <span className="insignia insignia-activa">Pagado</span>
                        ) : (
                          <span className="insignia">Pendiente</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>
      {q.data && (
        <Paginador
          page={q.data.page}
          pageSize={q.data.pageSize}
          total={q.data.total}
          onPage={(page) => cambiar({ page })}
        />
      )}
    </>
  );
}

export function EquiposPage() {
  return (
    <PermissionGate seccion="tienda">
      <Equipos />
    </PermissionGate>
  );
}
